import styles from './styles.module.css';
import { IBlogPost, IComplaint } from 'constants/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchSearch,
  SearchResults,
  SearchType,
  useSearchQuery,
} from 'hooks/api/search';
import { useRouter } from 'next/router';
import { Loader } from 'common';
import { CardsMedium } from 'components/slider/cards/medium';
import { Title, SubtitleCategory } from '../title';
import { CardsMini } from 'components/slider/cards/mini';
import { LoadMoreButton } from 'components/load-more-button';
import { TopicCommentSection as CommentSection } from 'components/template/topic-thread/comment-section';
import { CardsSkeleton } from 'components/slider/cards/medium';
import { Article } from 'components/template/home/news';

type Props = {
  searchText?: string;
};

export const Complaints = ({
  data,
  name,
}: {
  data: IComplaint[];
  name: string;
}) => {
  if (!data?.length) {
    return <CardsSkeleton />;
  }

  return (
    <>
      <SubtitleCategory text={name} />
      <CommentSection variant="complaint" items={data} />
    </>
  );
};

export const BlogList = ({
  data,
  name,
}: {
  data: IBlogPost[];
  name: string;
}) => {
  return (
    <div>
      <SubtitleCategory text={name} />
      <div className={styles.blogs__row}>
        {data.map((item) => (
          <Article
            data={item}
            key={item._id}
            className={styles.news__article_flex}
            textSize={'small'}
            arrow={false}
            widthImage={'box'}
          />
        ))}
      </div>
    </div>
  );
};

const PAGE_SIZE = 20;

type PageType = Extract<
  SearchType,
  'casinos' | 'slots' | 'complaints' | 'blogs'
>;

const PAGE_TYPES: PageType[] = ['casinos', 'slots', 'complaints', 'blogs'];

type MoreState = Partial<{ [K in PageType]: SearchResults[K] }>;

export const ResultSearch = ({ searchText }: Props) => {
  const router = useRouter();
  const search = (
    router.query.q2
      ? String(decodeURIComponent(router.query.q2?.toString()))
      : (searchText ?? '')
  ).toLowerCase();

  const { data, isFetching } = useSearchQuery(
    {
      searchQuery: search,
      type: PAGE_TYPES.join(','),
      limit: PAGE_SIZE,
    },
    { skip: !search }
  );

  // Pages loaded with "Load more", per type, on top of the first page
  const [more, setMore] = useState<MoreState>({});
  const [loadingType, setLoadingType] = useState<PageType | null>(null);

  useEffect(() => {
    setMore({});
  }, [search]);

  const items = useMemo(() => {
    const pick = <K extends PageType>(type: K): SearchResults[K] =>
      [
        ...((search && data?.results?.[type]) || []),
        ...(more[type] ?? []),
      ] as SearchResults[K];

    return {
      casinos: pick('casinos'),
      slots: pick('slots'),
      complaints: pick('complaints'),
      blogs: pick('blogs'),
    };
  }, [data, more, search]);

  const loadMore = useCallback(
    async (type: PageType) => {
      setLoadingType(type);
      try {
        const response = await fetchSearch({
          searchQuery: search,
          type,
          limit: PAGE_SIZE,
          offset: items[type].length,
        });
        setMore((prev) => ({
          ...prev,
          [type]: [...(prev[type] ?? []), ...(response?.results?.[type] ?? [])],
        }));
      } finally {
        setLoadingType(null);
      }
    },
    [items, search]
  );

  const renderLoadMore = (type: PageType) => {
    const total = data?.counts?.[type] ?? 0;
    const remaining = total - items[type].length;
    const isLoading = loadingType === type;

    return (
      remaining > 0 && (
        <div className={styles.load_more}>
          <LoadMoreButton
            isVisible
            isFetching={isLoading}
            onClick={() => !isLoading && loadMore(type)}
            description={isLoading ? 'Loading...' : `Load more (${remaining})`}
            variant="transparent"
          />
        </div>
      )
    );
  };

  const { casinos, slots, complaints, blogs } = items;

  return (
    <div className={styles.container}>
      {isFetching ? (
        <div className={styles.loader}>
          <Loader size={'big'} />
        </div>
      ) : (
        <>
          <Title text="Search Results" />
          {casinos.length > 0 && (
            <>
              <SubtitleCategory text={'Casinos'} />
              <CardsMedium data={casinos} />
              {renderLoadMore('casinos')}
            </>
          )}
          {slots.length > 0 && (
            <>
              <CardsMini
                data={slots}
                name={'Slots'}
                count={String(data?.counts?.slots ?? '')}
              />
              {renderLoadMore('slots')}
            </>
          )}
          {complaints.length > 0 && (
            <>
              <Complaints data={complaints} name={'Complaints'} />
              {renderLoadMore('complaints')}
            </>
          )}
          {blogs.length > 0 && (
            <>
              <BlogList data={blogs} name={'Content'} />
              {renderLoadMore('blogs')}
            </>
          )}
        </>
      )}
    </div>
  );
};
